/**
 * What the walk dispatch answered.
 *
 * This route returned an untyped envelope for the whole of its life, and the
 * page awaited it, discarded it, and said "Walk started" every time. A dispatch
 * the backend accepted while reporting it had claimed nothing rendered as a
 * success, and then nothing ever happened.
 *
 * It is `WalkDispatchView` now, and the two executions are named separately for
 * the reason the contract gives: discovery may be accepted while attribution is
 * not, and reporting them as one success hides exactly the case that leaves an
 * operator watching a walk that will never report. `basis` is the backend's own
 * words for what the dispatch did and did not authorise, and is shown verbatim.
 */

export interface WalkDispatch {
  /** Whether the answer is consistent with a walk actually starting. */
  started: boolean;
  /** What to tell the operator. */
  detail: string;
}

interface View {
  bucketName?: unknown;
  discoveryDispatched?: unknown;
  attributionDispatched?: unknown;
  basis?: unknown;
}

/** Envelope-or-payload, without assuming which was handed in. */
function payload(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const inner = v.data;
  if (inner && typeof inner === 'object') return inner as Record<string, unknown>;
  return v;
}

export function walkDispatchAnswer(value: unknown): WalkDispatch {
  const d = payload(value) as View | null;
  if (!d) return { started: true, detail: 'Walk started. It reports in the strip below as it runs.' };

  const attribution = d.attributionDispatched;
  const discovery = d.discoveryDispatched;
  /** The backend's sentence, which says more than any of ours would. */
  const basis = typeof d.basis === 'string' && d.basis.trim() ? ` ${d.basis.trim()}` : '';

  // Attribution is the execution that produces a walk. Discovery claims among
  // the buckets selected for sweeping and is not what the strip below follows,
  // so a response where only discovery was accepted is not a started walk.
  if (attribution === true) {
    return {
      started: true,
      detail: `Walk started. It reports in the strip below as it runs.${basis}`,
    };
  }
  if (attribution === false) {
    return {
      started: false,
      detail:
        `The backend accepted the request and did not start the attribution execution, which is the one that walks this ` +
        `bucket.${discovery === true ? ' The discovery execution was accepted.' : ''}${basis}`,
    };
  }

  /**
   * An answer this build cannot read.
   *
   * Older deployments answer the untyped envelope this route used to return, so
   * any boolean that came back false is still reported by name rather than
   * assumed away.
   */
  const declined = Object.entries(d as Record<string, unknown>)
    .filter(([, v]) => v === false)
    .map(([k]) => k);
  if (declined.length === 0) {
    return { started: true, detail: `Walk started. It reports in the strip below as it runs.${basis}` };
  }
  return {
    started: false,
    detail:
      `The backend accepted the request and reported ${declined.join(' and ')} as false, so this did not start ` +
      `everything a walk needs.${basis}`,
  };
}
